import React, { useContext } from 'react';
import { getThemeProvider } from './getThemeProvider';
import type { ThemeProvider, ThemeValueGetter } from 'fe-lib-theme-types';
import { ThemeContext } from './theme-context';

const { getThemeValue, subscribe } = getThemeProvider();

export interface WithThemeProps {
  $theme: ThemeProvider['getThemeValue'];
}

export function withHsTheme<
  C extends React.ComponentType<React.PropsWithChildren<any>>,
>(
  Comp: C,
): React.FunctionComponent<
  React.PropsWithChildren<
    C extends React.ComponentType<React.PropsWithChildren<infer P>> ? P : never
  >
> {
  const WithHsTheme = React.forwardRef((props: any, ref) => {
    const [, setTrigger] = React.useState(Date.now());
    const customThemeName = useContext(ThemeContext);

    React.useEffect(() => {
      // Subscribe to global theme changes. If it's swapped, we need to re-render.
      return subscribe(() => setTrigger(Date.now()));
    }, [setTrigger]);

    return (
      <Comp
        {...props}
        ref={ref}
        $theme={(getter: ThemeValueGetter) =>
          getThemeValue(getter, customThemeName)
        }
      />
    );
  }) as unknown as React.FunctionComponent<
    React.PropsWithChildren<
      C extends React.ComponentType<React.PropsWithChildren<infer P>>
        ? P
        : never
    >
  >;
  WithHsTheme.displayName = `WithHsTheme(${getDisplayName(Comp)})`;

  return WithHsTheme;
}

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
